import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { useConfirmPasswordReset, useValidateResetToken } from "../api/hooks";
import { AuthLayout } from "../components/Layouts/AuthLayout";
import { ConfirmResetPasswordForm } from "../components/Form/Forms/Auth/ConfirmResetPasswordForm";
import { Alert } from "../components/Alerts/Alert";
import { LoadingLayout } from "../components/Layouts/LoadingLayout";
import { motion } from "framer-motion";

export function ConfirmResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userId = searchParams.get("uid");
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  const { data, isLoading: isValidateResetTokenLoading, isError: isValidateError } =
    useValidateResetToken(token, userId);

  const { mutate: confirmPassword, isLoading, isError, error } = useConfirmPasswordReset();

  const onSubmit = (data) => {
    confirmPassword(
      { newPassword: data.password, uid: userId, token: token },
      { onSuccess: () => { setSuccess(true); navigate("/login"); } }
    );
  };

  if (!token || !userId) {
    return (
      <AuthLayout>
        <Alert message="Invalid password reset link." />
      </AuthLayout>
    );
  }

  if (isValidateResetTokenLoading) {
    return (
      <AuthLayout>
        <LoadingLayout />
      </AuthLayout>
    );
  }

  if (isValidateError || !data?.valid) {
    return (
      <AuthLayout>
        <Alert message="This password reset link is invalid or expired." />
        <div className="mt-2 text-sm text-center">
          <Link to="/reset" className="text-blue hover:underline">
            Return to Forgot Password
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white/90 backdrop-blur-xl rounded-xl p-10 border border-gray-200"
      >
        {success ? (
          <div className="text-center">
            <h2 className="text-2xl font-semibold">Password successfully reset</h2>
            <p className="mt-4 text-gray-600 text-sm">
              You can now log in with your new password.
            </p>
            <div className="mt-6">
              <Link to="/login" className="text-blue font-medium hover:underline text-sm">
                Return to sign in
              </Link>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-semibold text-center">Set a new password</h2>
            <div className="mt-6">
              <ConfirmResetPasswordForm onSubmit={onSubmit} isLoading={isLoading} />
            </div>
            {isError && <Alert message={error?.response?.data?.message || "Something went wrong. Please try again."} />}
          </div>
        )}
      </motion.div>
    </AuthLayout>
  );
}